import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import Utils, { GetHelper, payloadsEqual } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import UIDefaultTheme from 'src/themes';

export type CompanyTaxValues = {
  citizen_us: string;
  permanent_resident_us: boolean;
  has_green_card: boolean;
  us_tax_return: boolean;
  description_us_tax: string;

  signed: boolean;
  id?: number;
};

@Component({
  selector: 'app-signup-unified-company-tax',
  templateUrl: './signup-unified-company-tax.component.html',
  styleUrls: ['./signup-unified-company-tax.component.scss'],
})
export class SignupUnifiedCompanyTaxComponent implements OnInit, OnDestroy {
  form = this.formBuilder.group({
    citizen_us: [null, [Validators.required]],
    permanent_resident_us: [null, [Validators.required]],
    has_green_card: [null, [Validators.required]],
    us_tax_return: [null, [Validators.required]],
    description_us_tax: [null, []],
    signed: [false, [Validators.requiredTrue]],
    ssn: [null, [Validators.required, Validators.maxLength(16)]],
    ssn_free: [false, []],
  });

  event: any;
  data: CompanyTaxValues;
  fullScreenLoading = false;
  companyShortName = UIDefaultTheme.brand.name;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, any> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private signupService: SignupService,
    private api: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute,
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    this.event = this.signupService.sendCompanyData.subscribe((value) => {
      if (value === 'tax') {
        this.sendData();
      }
    });

    this.getData();

    this.form.statusChanges.subscribe((status) => {
      Utils.getErrors(this.form);
      this.signupService.setCompanyFormStatus(
        'tax',
        status === 'VALID' && this.form.value.signed,
      );
    });

    this.form.controls.ssn_free.valueChanges.subscribe((value) => {
      if (value) {
        this.form.controls.ssn.setValue(null);
        this.form.controls.ssn.clearValidators();
      } else {
        this.form.controls.ssn.setValidators([
          Validators.required,
          Validators.maxLength(16),
        ]);
      }
      this.form.controls.ssn.updateValueAndValidity();
    });

    this.form.controls.us_tax_return.valueChanges.subscribe((value) => {
      if (value === 'yes') {
        this.form.controls.description_us_tax.setValidators([
          Validators.required,
          Validators.minLength(1),
        ]);
      } else {
        this.form.controls.description_us_tax.setValue(null);
        this.form.controls.description_us_tax.clearValidators();
      }
      this.form.controls.description_us_tax.updateValueAndValidity();
    });

    this.form.controls.description_us_tax.valueChanges.subscribe((value) => {
      if (value) {
        value.trim();
        if (value.trim().length === 0) {
          this.form.controls.description_us_tax.setErrors({ whitespace: true });
        } else {
          this.form.controls.description_us_tax.setErrors(null);
        }
      }
    });
  }

  private buildPayloadFromForm(): Record<string, any> {
    const valuesHelper = new GetHelper(this.form.value);
    return {
      citizen_us: valuesHelper.get('citizen_us') === 'yes' ? true : false,
      permanent_resident_us:
        valuesHelper.get('permanent_resident_us') === 'yes' ? true : false,
      has_green_card:
        valuesHelper.get('has_green_card') === 'yes' ? true : false,
      us_tax_return: valuesHelper.get('us_tax_return') === 'yes' ? true : false,
      description_us_tax: valuesHelper.get('description_us_tax'),
      ssn: valuesHelper.get('ssn'),
      signature: valuesHelper.get('signature'),
    };
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      this.toast.show(
        'error',
        'Erro',
        'UUID do registro não encontrado na URL',
      );
      return;
    }

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      this.fullScreenLoading = false;
      await this.signupService.updateUserStepCompany(
        'register',
        'attachments',
        routeId,
      );
      return;
    }

    this.signupService.loading = true;
    this.fullScreenLoading = true;

    try {
      if (this.data && this.data.id) {
        await this.api.put({
          route: `api/registration/investor-profile-quiz/${this.data.id}/`,
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });
      } else {
        await this.api.post({
          route: 'api/registration/investor-profile-quiz/',
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });
      }

      this.initialPayload = this.buildPayloadFromForm();
      await this.signupService.updateUserStepCompany(
        'register',
        'attachments',
        routeId,
      );
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao salvar os dados',
      );
      this.signupService.loading = false;
      this.fullScreenLoading = false;
    } finally {
      this.signupService.loading = false;
      this.fullScreenLoading = false;
    }
  }

  async getData() {
    try {
      this.fullScreenLoading = true; // Ativar spinner de tela cheia

      // Sempre pegar o UUID da URL
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        return;
      }

      const response = await this.api.get({
        route: 'api/registration/investor-profile-quiz/',
        params: { register_uuid: routeId },
        token: true,
      });

      const responseData = response?.data;
      const taxList = Array.isArray(responseData?.data)
        ? responseData.data
        : responseData;
      let taxData = null;

      // Se data é um array, pegar o item com o maior id
      if (Array.isArray(taxList) && taxList.length > 0) {
        taxData = taxList.reduce((prev, current) => {
          return prev.id > current.id ? prev : current;
        });
      }
      // Se data é um objeto válido, usar diretamente
      else if (
        taxList &&
        !Array.isArray(taxList) &&
        typeof taxList === 'object' &&
        Object.keys(taxList).length > 0
      ) {
        taxData = taxList;
      }

      // Se encontrou dados válidos, preencher o formulário
      if (taxData) {
        const hasSavedTaxData = Array.isArray(taxList)
          ? taxList.length > 0
          : true;

        this.form.patchValue({
          signed: hasSavedTaxData,
          permanent_resident_us: taxData.permanent_resident_us
            ? 'yes'
            : taxData.permanent_resident_us === false
              ? 'no'
              : null,
          citizen_us: taxData.citizen_us
            ? 'yes'
            : taxData.citizen_us === false
              ? 'no'
              : null,
          has_green_card: taxData.has_green_card
            ? 'yes'
            : taxData.has_green_card === false
              ? 'no'
              : null,
          us_tax_return: taxData.us_tax_return
            ? 'yes'
            : taxData.us_tax_return === false
              ? 'no'
              : null,
          description_us_tax: taxData.description_us_tax || null,
          ssn: taxData.ssn || null,
          ssn_free: !taxData.ssn || false,
        });
        this.form.updateValueAndValidity();

        this.data = taxData;
        this.initialPayload = this.buildPayloadFromForm();
      } else {
        this.form.reset();
        this.form.controls.signed.setValue(false);
        this.form.controls.ssn_free.setValue(false);
        this.data = null;
        this.initialPayload = null;
      }
    } catch (error) {
      this.initialPayload = null;
      if (error.status !== 404) {
        this.toast.show(
          'error',
          'Erro',
          error.error?.message || 'Erro ao carregar dados fiscais',
        );
      }
    } finally {
      this.fullScreenLoading = false; // Desativar spinner de tela cheia
    }
  }
}
